import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Phone, PhoneIncoming, PhoneOutgoing, Clock, Download } from 'lucide-react';
import { config } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
interface Call {
  id: number;
  call_sid: string;
  from_number: string;
  to_number: string;
  direction: 'inbound' | 'outbound';
  call_type: string;
  status: string;
  duration_seconds: number;
  has_voicemail: boolean;
  initiated_at: string;
  recording_url?: string;
}

interface CallHistoryProps {
  referralId?: string;
  providerId?: string;
  onRefReady?: (ref: { fetchCallHistory: () => void }) => void;
}

export const CallHistory: React.FC<CallHistoryProps> = ({
  referralId,
  providerId,
  onRefReady
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [calls, setCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCallHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      let url = `${config.weaveinApi.baseUrl}/api/call-history?`;
      if (referralId) url += `referral_id=${referralId}&`;
      if (providerId) url += `provider_id=${providerId}&`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || '1'
        }
      });

      const data = await response.json();
      setCalls(data.calls || []);

    } catch (error) {
      console.error('Failed to fetch call history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCallHistory();
  }, [referralId, providerId]);

  // Expose fetchCallHistory to parent via callback
  useEffect(() => {
    if (onRefReady) onRefReady({ fetchCallHistory });
  }, [onRefReady]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (number: string) => {
    // Format +14155551234 to (415) 555-1234
    const cleaned = number.replace(/\D/g, '');
    const match = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return number;
  };

  if (loading) {
    return <div className="text-center py-8">{t('communications.loading_call_history', 'Loading call history...')}</div>;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Call History ({calls.length})</h3>

      {calls.length === 0 ? (
        <p className="text-gray-500 text-center py-8">{t('communications.no_calls_yet', 'No calls yet')}</p>
      ) : (
        <div className="space-y-2">
          {calls.map((call) => (
            <div
              key={call.id}
              className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {call.direction === 'outbound' ? (
                    <PhoneOutgoing className="w-5 h-5 text-green-600" />
                  ) : (
                    <PhoneIncoming className="w-5 h-5 text-blue-600" />
                  )}

                  <div>
                    <p className="font-medium">
                      {call.direction === 'outbound' ? 'To: ' : 'From: '}
                      {formatPhoneNumber(
                        call.direction === 'outbound' ? call.to_number : call.from_number
                      )}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-3 h-3" />
                      {fmt.dateTime(call.initiated_at)}
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    call.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : call.status === 'no_answer'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-600'
                  }`}>
                    {call.status.replace('_', ' ')}
                  </span>

                  {call.duration_seconds > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      Duration: {formatDuration(call.duration_seconds)}
                    </p>
                  )}
                </div>
              </div>

              {call.recording_url && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{t('communications.call_recording', 'Call Recording')}</span>
                    <a
                      href={`${call.recording_url}${call.recording_url.includes('?') ? '&' : '?'}token=${localStorage.getItem('auth_token') || ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      {t('communications.download', 'Download')}
                    </a>
                  </div>
                  <audio
                    controls
                    className="w-full h-10"
                    preload="metadata"
                  >
                    <source src={`${call.recording_url}${call.recording_url.includes('?') ? '&' : '?'}token=${localStorage.getItem('auth_token') || ''}`} type="audio/mpeg" />
                    {t('communications.your_browser_does_not_support', 'Your browser does not support audio playback.')}
                  </audio>
                </div>
              )}

              {call.has_voicemail && (
                <div className="mt-2">
                  <span className="text-sm text-blue-600 font-medium">
                    📧 Has voicemail
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
